import type { ReactNode } from "react";

import { I } from "./icons";

/**
 * A 44px pill (SNIPPETS §14) for filters and quick-add.
 *
 * When to use: library filters (`selected` — on shows a check and brand tint) and Food quick-add
 * (no `selected`, an `icon` such as `I.plus`). Put a row of them in a `ChipRow`.
 */
export function Chip({
  children,
  onClick,
  selected,
  icon,
  disabled,
  ariaLabel,
  size,
  own,
}: {
  children: ReactNode;
  onClick?: () => void;
  /** a filter's state; omit for an action chip, which then carries no aria-pressed */
  selected?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  /** when the words on the chip do not say what a tap does ("Steel 1 L bottle: choose how much") */
  ariaLabel?: string;
  /** "lg": the question screens' 48px chip at 17px (kit §28) */
  size?: "lg";
  /** Something the person added themselves (kit §36): on colours, the close icon after the label,
   *  and a tap removes it — so it carries no aria-pressed and needs an `ariaLabel` saying so. */
  own?: boolean;
}) {
  const cls = `k-chip${size === "lg" ? " k-chip--lg" : ""}${own ? " k-chip--own" : ""}`;
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      aria-pressed={own ? undefined : selected}
      aria-label={ariaLabel}
      disabled={disabled}
    >
      {own ? null : selected ? I.tick : icon}
      {children}
      {own ? I.close : null}
    </button>
  );
}

/** A sideways-scrolling row of chips that bleeds to the screen edge; the last chip is cut by the gutter. */
export function ChipRow({ children, ariaLabel }: { children: ReactNode; ariaLabel?: string }) {
  return (
    <div className="k-chiprow" role="group" aria-label={ariaLabel}>
      {children}
    </div>
  );
}
